"use client";

import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";

interface GetStartedProps {
  goToNextStep: () => void;
}

// Animation variants for the list items
const listItemVariants = {
  initial: { opacity: 0, x: -20 },
  animate: (custom: number) => ({
    opacity: 1,
    x: 0,
    transition: { delay: 0.1 * custom, duration: 0.3 },
  }),
};

export default function GetStarted({ goToNextStep }: GetStartedProps) {
  const { t } = useTranslation(["common", "profile"]);

  return (
    <ScrollArea className="h-full w-full">
      <div className="mx-auto w-full max-w-7xl p-4 sm:p-6">
        {/* On mobile stack sections vertically; on desktop use a 1/3 + 2/3 split */}
        <div className="grid grid-cols-1 gap-3 sm:gap-4 lg:grid-cols-2">
          {/* Left side: colorful tips cards */}
          <div className="hidden lg:col-span-1 lg:flex lg:flex-col lg:justify-center lg:space-y-4">
            {[0, 1, 2, 3].map((index) => (
              <motion.div
                key={index}
                animate="animate"
                className="bg-foreground/30 relative overflow-hidden rounded-xl p-4 backdrop-blur-sm"
                custom={index + 1}
                initial="initial"
                variants={listItemVariants}
              >
                <div
                  className={`absolute top-1/2 left-0 h-full w-full -translate-x-1/6 -translate-y-1/2 rounded-full bg-gradient-to-br blur-3xl transition-colors duration-700 ${
                    index === 0
                      ? "from-blue-500/20 to-cyan-500/20"
                      : index === 1
                        ? "from-purple-500/20 to-pink-500/20"
                        : index === 2
                          ? "from-emerald-500/20 to-green-500/20"
                          : "from-orange-500/20 to-amber-500/20"
                  }`}
                />
                <div className="relative z-50 flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm leading-tight font-semibold">
                      {index === 0
                        ? t("profile:getStarted.step1Title", "Complete Your Profile")
                        : index === 1
                          ? t("profile:getStarted.step2Title", "Your Professional Card")
                          : index === 2
                            ? t("profile:getStarted.step3Title", "Stand Out From the Crowd")
                            : t("profile:getStarted.step4Title", "Encourage Students to Pick You")}
                    </h3>
                    <p className="text-muted-foreground mt-1 text-xs leading-relaxed">
                      {index === 0
                        ? t(
                            "profile:getStarted.step1Description",
                            "To complete your onboarding process, you need to create a profile. Your profile will be the foundation for students finding and choosing your services.",
                          )
                        : index === 1
                          ? t(
                              "profile:getStarted.step2Description",
                              "Your profile will be like a business card. Add useful and valid information about yourself to help students choose you.",
                            )
                          : index === 2
                            ? t(
                                "profile:getStarted.step3Description",
                                "Add a professional photo and detailed description of your teaching methods to make your profile memorable.",
                              )
                            : t(
                                "profile:getStarted.step4Description",
                                "Highlight your strengths, reviews and a clear offer to help students choose you.",
                              )}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Right side: illustration and primary CTA */}
          <div className="col-span-1 flex items-start justify-center">
            <div className="w-full space-y-4 sm:space-y-6">
              <motion.div
                animate={{ opacity: 1, y: 0 }}
                className="relative overflow-hidden rounded-xl"
                initial={{ opacity: 0, y: 10 }}
                transition={{ duration: 0.3 }}
              >
                <img
                  alt={t("profile:getStarted.illustrationAlt", "Profile setup illustration")}
                  className="h-auto w-full object-contain select-none max-xl:max-h-120"
                  src={"https://public-assets.ximly.app/tutor-profile-help.webp"}
                />
              </motion.div>

              {/* Mobile tips under the image */}
              <div className="lg:hidden">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {[0, 1, 2, 3].map((index) => (
                    <motion.div
                      key={`m-tip-${index}`}
                      animate="animate"
                      className="bg-foreground/30 relative overflow-hidden rounded-xl p-4 backdrop-blur-sm"
                      custom={index + 1}
                      initial="initial"
                      variants={listItemVariants}
                    >
                      <div
                        className={`absolute top-1/2 left-0 h-full w-full -translate-x-1/6 -translate-y-1/2 rounded-full bg-gradient-to-br blur-3xl transition-colors duration-700 ${
                          index === 0
                            ? "from-blue-500/20 to-cyan-500/20"
                            : index === 1
                              ? "from-purple-500/20 to-pink-500/20"
                              : index === 2
                                ? "from-emerald-500/20 to-green-500/20"
                                : "from-orange-500/20 to-amber-500/20"
                        }`}
                      />
                      <div className="relative z-50 flex items-start gap-3">
                        <div className="min-w-0 flex-1">
                          <h3 className="text-sm leading-tight font-semibold">
                            {index === 0
                              ? t("profile:getStarted.step1Title", "Complete Your Profile")
                              : index === 1
                                ? t("profile:getStarted.step2Title", "Your Professional Card")
                                : index === 2
                                  ? t("profile:getStarted.step3Title", "Stand Out From the Crowd")
                                  : t(
                                      "profile:getStarted.step4Title",
                                      "Encourage Students to Pick You",
                                    )}
                          </h3>
                          <p className="text-muted-foreground mt-1 text-xs leading-relaxed">
                            {index === 0
                              ? t(
                                  "profile:getStarted.step1Description",
                                  "To complete your onboarding process, you need to create a profile. Your profile will be the foundation for students finding and choosing your services.",
                                )
                              : index === 1
                                ? t(
                                    "profile:getStarted.step2Description",
                                    "Your profile will be like a business card. Add useful and valid information about yourself to help students choose you.",
                                  )
                                : index === 2
                                  ? t(
                                      "profile:getStarted.step3Description",
                                      "Add a professional photo and detailed description of your teaching methods to make your profile memorable.",
                                    )
                                  : t(
                                      "profile:getStarted.step4Description",
                                      "Highlight your strengths, reviews and a clear offer to help students choose you.",
                                    )}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="col-span-full w-full">
            <Button
              className="group w-full px-8 py-6 text-lg"
              size="lg"
              variant="foreground"
              onClick={goToNextStep}
            >
              {t("profile:getStarted.createProfile", "Create Profile")}
              <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Button>
          </div>
        </div>
      </div>
    </ScrollArea>
  );
}
